import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import CardinalShareButtons from './CardinalShareButtons';
import MetaTags from './MetaTags';
import '../styles/CardinalDetail.css';

const CardinalDetail = () => {
  const { slug } = useParams();
  const [cardinal, setCardinal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  useEffect(() => {
    const fetchCardinal = async () => {
      try {
        const response = await axios.get(`/api/cardinaux/slug/${slug}`);
        setCardinal(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement du cardinal:', err);
        setError('Cardinal non trouvé');
        setLoading(false);
      }
    };
    
    fetchCardinal();
  }, [slug]);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl) => {
    if (!originalUrl || imageError) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = () => {
    console.log("Erreur de chargement d'image, utilisation de l'image par défaut");
    setImageError(true);
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  // Fonction pour calculer l'âge
  const calculateAge = (birthDate) => {
    if (!birthDate) return 'Non spécifié';
    
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) return <div className="loading">Chargement du cardinal...</div>;
  if (error) return <div className="error">{error}</div>;
  
  // Préparer les données pour les meta tags
  // Construction du titre de la page avec le nom du cardinal
  const pageTitle = `Cardinal ${cardinal.nomComplet}`;
  
  // Construction de la description pour les meta tags
  const description = `${cardinal.titreHonorifique} ${cardinal.nomComplet}, ${cardinal.age || calculateAge(cardinal.dateNaissance)} ans, cardinal de l'Église catholique, ${cardinal.paysService}. ${cardinal.statutVotant === 'OUI' ? 'Électeur au conclave.' : 'Non-électeur au conclave.'}`;
  
  // Construire l'URL absolue pour l'image (pour les méta-tags)
  const getMetaImageUrl = () => {
    if (!cardinal.photo) {
      return 'https://conclave2025.fr/images/cardinal-default.png';
    }
    
    // Extraire uniquement le nom du fichier sans paramètres
    const filename = cardinal.photo.split('/').pop().split('?')[0];
    
    // Construire une URL absolue sans paramètres
    return `https://conclave2025.fr/uploads/cardinaux/${filename}`;
  };
  
  // Construire l'URL canonique pour cette page
  const canonicalUrl = `https://conclave2025.fr/cardinaux/${slug}`;

  return (
    <>
      {/* Ajouter les meta tags spécifiques à ce cardinal */}
      <MetaTags 
        title={pageTitle}
        description={description}
        image={getMetaImageUrl()}
        url={canonicalUrl}
      />
      
      <div className="cardinal-detail-container">
        <Link to="/cardinaux" className="back-button">
          ← Retour à l'annuaire
        </Link>
        
        <div className="detail-header">
          <div className="detail-photo">
            <img 
              src={getApiImageUrl(cardinal.photo)} 
              alt={cardinal.nomComplet} 
              onError={handleImageError}
            />
            
            {cardinal.papabile && (
              <div className="papabile-badge-large">
                Papabile
              </div>
            )}
            
            {cardinal.statutVotant === 'OUI' && (
              <div className="votant-badge-large">
                Électeur
              </div>
            )}
          </div>
          
          <div className="detail-info">
            <h1>{cardinal.nomComplet}</h1>
            <h2>{cardinal.titre || `${cardinal.ordre}`}</h2>
            
            <div className="detail-metadata">
              <p><strong>Âge:</strong> {cardinal.age || calculateAge(cardinal.dateNaissance)} ans ({formatDate(cardinal.dateNaissance)})</p>
              <p><strong>Pays de naissance:</strong> {cardinal.paysNaissance}</p>
              <p><strong>Pays de service:</strong> {cardinal.paysService}</p>
              <p><strong>Continent:</strong> {cardinal.continent}</p>
              <p><strong>Nationalité:</strong> {cardinal.nationalite}</p>
              <p><strong>Créé cardinal par:</strong> {cardinal.creePar} ({formatDate(cardinal.dateCreation)})</p>
              <p><strong>Statut:</strong> {cardinal.statutVotant === 'OUI' ? 'Électeur au conclave' : 'Non-électeur au conclave'}</p>
            </div>
          </div>
        </div>
        
        <div className="detail-content">
          {cardinal.biographie && (
            <div className="detail-section">
              <h3>Biographie</h3>
              <p>{cardinal.biographie}</p>
            </div>
          )}
          
          {cardinal.formation && (
            <div className="detail-section">
              <h3>Formation</h3>
              <p>{cardinal.formation}</p>
            </div>
          )}
          
          {cardinal.languesParlees && cardinal.languesParlees.length > 0 && (
            <div className="detail-section">
              <h3>Langues parlées</h3>
              <ul className="languages-list">
                {Array.isArray(cardinal.languesParlees) ? (
                  cardinal.languesParlees.map((langue, index) => (
                    <li key={index}>{langue}</li>
                  ))
                ) : (
                  <li>{cardinal.languesParlees}</li>
                )}
              </ul>
            </div>
          )}
          
          {cardinal.fonctions && cardinal.fonctions.length > 0 && (
            <div className="detail-section">
              <h3>Fonctions</h3>
              <ul className="functions-list">
                {cardinal.fonctions.map((fonction, index) => (
                  <li key={index}>
                    <strong>{fonction.titre}</strong> - {fonction.organisation}
                    <span className="dates">
                      {formatDate(fonction.debut)} 
                      {fonction.fin ? ` - ${formatDate(fonction.fin)}` : ' - Présent'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {cardinal.publications && cardinal.publications.length > 0 && (
            <div className="detail-section">
              <h3>Publications</h3>
              <ul className="publications-list">
                {Array.isArray(cardinal.publications) ? (
                  cardinal.publications.map((publication, index) => (
                    <li key={index}>{publication}</li>
                  ))
                ) : (
                  <li>{cardinal.publications}</li>
                )}
              </ul>
            </div>
          )}
          
          {cardinal.papabile && (
            <div className="detail-section">
              <h3>Statut de papabile</h3>
              <div className="papabile-section">
                <p>
                  {cardinal.nomComplet} est considéré comme l'un des papabile, c'est-à-dire
                  un candidat potentiel au trône pontifical.
                </p>
                
                {(cardinal.raisonsForPapabile && cardinal.raisonsForPapabile.length > 0) || 
                 (cardinal.raisonsAgainstPapabile && cardinal.raisonsAgainstPapabile.length > 0) ? (
                  <div className="papabile-reasons">
                    {cardinal.raisonsForPapabile && cardinal.raisonsForPapabile.length > 0 && (
                      <div className="reasons-for">
                        <h4>Atouts comme papabile</h4>
                        <ul>
                          {Array.isArray(cardinal.raisonsForPapabile) ? (
                            cardinal.raisonsForPapabile.map((raison, index) => (
                              <li key={index}>{raison}</li>
                            ))
                          ) : (
                            <li>{cardinal.raisonsForPapabile}</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {cardinal.raisonsAgainstPapabile && cardinal.raisonsAgainstPapabile.length > 0 && (
                      <div className="reasons-against">
                        <h4>Facteurs limitants</h4>
                        <ul>
                          {Array.isArray(cardinal.raisonsAgainstPapabile) ? (
                            cardinal.raisonsAgainstPapabile.map((raison, index) => (
                              <li key={index}>{raison}</li>
                            ))
                          ) : (
                            <li>{cardinal.raisonsAgainstPapabile}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
        
        <CardinalShareButtons cardinal={cardinal} />
      </div>
    </>
  );
};

export default CardinalDetail;